/**
 * Gallery scene: secondary dock enter styles (?test=maater-ui#panel-motion)
 *
 * Pick a style - preference persists and drives SecondaryPanel content enter.
 * Column width stays instant (no react-resizable-panels width tween).
 */
import { useEffect, useState } from 'react'
import { FileText, Info, PanelRight } from 'lucide-react'
import { useShellPanelMotionStyle } from '@/hooks/use-shell-panel-motion'
import {
  SHELL_PANEL_MOTION_MS,
  SHELL_PANEL_MOTION_STYLES,
  shellPanelEnterClass,
  type ShellPanelMotionStyleId,
} from '@/lib/shell-panel-motion'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export function PanelMotionScene() {
  const [style, setStyle] = useShellPanelMotionStyle()
  const [open, setOpen] = useState(true)
  const [enterKey, setEnterKey] = useState(0)

  // Replay enter when style changes or dock re-opens
  useEffect(() => {
    if (!open) return
    setEnterKey((k) => k + 1)
  }, [style, open])

  const previewEnter = shellPanelEnterClass(style, 'right')

  return (
    <div className="space-y-4" data-testid={testIds.maaterUiGallery.panelMotionScene}>
      <p className="text-sm" style={{ color: 'var(--ink)' }}>
        Secondary docks (Info, Continuity preview) mount at target width - never fake
        0%→N% resize. Motion is <strong className="font-medium">content enter only</strong>.
        Shell left/right collapse stays an instant geometry swap. Tokens:{' '}
        <code className="text-xs">@/lib/shell-panel-motion</code> ({SHELL_PANEL_MOTION_MS}ms
        default).
      </p>

      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>
          Choose enter style
        </p>
        <div
          className="grid gap-2 sm:grid-cols-2"
          data-testid={testIds.maaterUiGallery.panelMotionStylePicker}
        >
          {SHELL_PANEL_MOTION_STYLES.map((opt) => {
            const selected = style === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={testIds.maaterUiGallery.panelMotionStyle(opt.id)}
                onClick={() => setStyle(opt.id as ShellPanelMotionStyleId)}
                className={cn(
                  'rounded-md border px-3 py-2 text-left transition-colors',
                  selected
                    ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/30'
                    : 'hover:bg-muted/60',
                )}
                style={{ borderColor: selected ? undefined : 'var(--line)' }}
              >
                <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                  {opt.label}
                  {selected ? (
                    <span className="ml-1.5 text-[10px] font-normal text-primary">active</span>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs" style={{ color: 'var(--ink-soft)' }}>
                  {opt.summary}
                </p>
              </button>
            )
          })}
        </div>
      </div>

      <div
        className="overflow-hidden rounded-lg border"
        style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
        data-testid={testIds.maaterUiGallery.panelMotionDemo}
      >
        <div
          className="flex items-center justify-between border-b px-3 py-2"
          style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
        >
          <span className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>
            Document mock
          </span>
          <button
            type="button"
            data-testid={testIds.maaterUiGallery.panelMotionToggle}
            onClick={() => setOpen((v) => !v)}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
              open
                ? 'bg-primary/15 text-primary ring-1 ring-primary/40'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
            aria-pressed={open}
            aria-label="Toggle info dock"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>

        <div className="flex min-h-[220px]">
          <div className="min-w-0 flex-1 space-y-2 p-4">
            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--ink)' }}>
              <FileText className="h-4 w-4 text-muted-foreground" />
              Weekly priorities
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
              Main column keeps its slot. Toggle Info - dock width snaps to target; only the
              pane body uses the selected enter style.
            </p>
            <button
              type="button"
              className="text-xs text-primary underline-offset-2 hover:underline"
              onClick={() => {
                setOpen(false)
                requestAnimationFrame(() => setOpen(true))
              }}
            >
              Replay enter
            </button>
          </div>

          {open ? (
            <>
              <div className="w-px shrink-0 bg-border" aria-hidden />
              <aside
                key={enterKey}
                className={cn(
                  'flex w-[240px] shrink-0 flex-col border-l',
                  previewEnter,
                )}
                style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
                data-testid={testIds.maaterUiGallery.panelMotionDock}
              >
                <div
                  className="flex h-10 items-center gap-1.5 border-b px-3 text-xs font-semibold"
                  style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                >
                  <PanelRight className="h-3.5 w-3.5 text-muted-foreground" />
                  Info
                </div>
                <div className="space-y-2 p-3 text-xs" style={{ color: 'var(--ink-soft)' }}>
                  <p style={{ color: 'var(--ink)' }} className="font-medium">
                    Document details
                  </p>
                  <p>Path · tags · sync · history live here in production.</p>
                  <p className="text-[10px] uppercase tracking-wide opacity-70">
                    Style: {style}
                  </p>
                </div>
              </aside>
            </>
          ) : null}
        </div>
      </div>

      <ul className="list-disc space-y-1 pl-5 text-xs" style={{ color: 'var(--ink-soft)' }}>
        <li>
          Reject: <code>resize(0)</code> then <code>rAF → resize(N)</code> (two-frame jump)
        </li>
        <li>
          Prefer: mount at <code>PANEL_SIZES.*_DEFAULT</code> + content enter class
        </li>
        <li>
          <code>prefers-reduced-motion</code> maps nudge/crisp → quiet fade
        </li>
        <li>Art until locked - pick a style here; production SecondaryPanel follows</li>
      </ul>
    </div>
  )
}
